// client/src/components/pages/LexiStar/Billing/PlanEditor.tsx
//
// The billing plan of one organisation (ST-00207 stream A; m4b-contracts §2.1/§2.4 PUT /plan):
// plan code, how the trucks are billed (ST-00241: the flat monthly rate per truck, or the old
// truck-days rule kept as the switch back), the rate per truck per month, included
// pass-through messages, VAT. Global admins only (the page shows it when GET /plan says
// can_manage). The form is checked the way the API checks it; the write carries the version
// read, so a change made meanwhile is reported instead of overwritten. A new rate or basis
// applies to the drafts from the next rebuild and to a month when it is finalised — a final
// statement never changes.

import { useEffect, useState } from 'react';
import { FaSave } from 'react-icons/fa';
import { cardClasses } from '../Dashboard/analyticsAtoms';
import { ThemedSelect } from '../../../../types/commonComponents';
import { formatMiningDateTime } from '../../../../utils/dateUtils';
import { billingErrorCode, billingErrorText, savePlan } from '../../../../services/lexistarBilling';
import type { BillingBasis, BillingPlan } from '../../../../types/lexistar-billing';
import { BASIS_LABELS, draftOfPlan, validatePlanDraft, type PlanDraft } from './billingLogic';

const inputClass =
  'w-full px-2.5 py-2 border border-slate-300 rounded-md bg-surface text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

export default function PlanEditor({
  plan,
  tenantId,
  tenantName,
  onSaved,
  onReload,
}: {
  plan: BillingPlan;
  /** The organisation the plan is for (sent as tenant_id). */
  tenantId: number;
  tenantName: string | null;
  onSaved: (plan: BillingPlan) => void;
  /** Read the plan again (someone changed it meanwhile). */
  onReload: () => void;
}) {
  const [draft, setDraft] = useState<PlanDraft>(() => draftOfPlan(plan));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Start a fresh draft only when a different plan arrives (another organisation, or a new stored
  // version after a save or a stale-version reload). A re-read that returns the same plan must not
  // wipe what the admin is typing.
  const planKey = `${tenantId}:${plan.tenant_id}:${plan.version}`;
  useEffect(() => {
    setDraft(draftOfPlan(plan));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey]);

  const set = (patch: Partial<PlanDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setNotice(null);
  };

  const save = async () => {
    const checked = validatePlanDraft(draft);
    if (!checked.ok) {
      setError(checked.error);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await savePlan({ ...checked.value, tenant_id: tenantId, version: plan.version });
      setNotice(res.changed ? 'Plan saved. Drafts use it from their next rebuild; final statements do not change.' : 'Nothing changed.');
      onSaved(res.plan);
    } catch (err) {
      if (billingErrorCode(err) === 'stale_version') {
        setError('Someone changed this plan while you were editing it. The page shows the current plan now; check it and save again.');
        onReload();
      } else {
        setError(billingErrorText(err, 'The plan could not be saved.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const id = (k: string) => `billing-plan-${k}`;
  return (
    <section className={`${cardClasses} p-4 space-y-3`} aria-labelledby={id('title')}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={id('title')} className="text-sm font-semibold text-slate-900">
          Billing plan{tenantName ? ` · ${tenantName}` : ''}
        </h2>
        <p className="text-[11px] text-slate-500">
          {plan.version > 0 ? `Version ${plan.version}${plan.updated_at ? ` · changed ${formatMiningDateTime(plan.updated_at)}` : ''}${plan.updated_by ? ` by ${plan.updated_by}` : ''}` : 'No plan stored yet: these are the defaults.'}
        </p>
      </div>
      <form
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end"
        aria-label="Billing plan"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div>
          <label htmlFor={id('code')} className="block text-[11px] font-medium text-slate-600 mb-1">
            Plan code
          </label>
          <input id={id('code')} value={draft.plan_code} onChange={(e) => set({ plan_code: e.target.value })} className={inputClass} autoComplete="off" />
        </div>
        <div>
          <label htmlFor={id('basis')} className="block text-[11px] font-medium text-slate-600 mb-1">
            Trucks are billed
          </label>
          <ThemedSelect
            id={id('basis')}
            aria-label="Trucks are billed"
            value={draft.billing_basis}
            onChange={(e) => set({ billing_basis: e.target.value as BillingBasis })}
          >
            <option value="flat_monthly">{BASIS_LABELS.flat_monthly}</option>
            <option value="truck_days">{BASIS_LABELS.truck_days}</option>
          </ThemedSelect>
        </div>
        <div>
          <label htmlFor={id('rate')} className="block text-[11px] font-medium text-slate-600 mb-1">
            Rate per truck (R / month)
          </label>
          <input
            id={id('rate')}
            type="number"
            inputMode="decimal"
            min={0}
            step={0.01}
            value={draft.asset_rate_zar}
            onChange={(e) => set({ asset_rate_zar: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={id('included')} className="block text-[11px] font-medium text-slate-600 mb-1">
            Included messages / month
          </label>
          <input
            id={id('included')}
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={draft.included_messages}
            onChange={(e) => set({ included_messages: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={id('vat')} className="block text-[11px] font-medium text-slate-600 mb-1">
            VAT (%)
          </label>
          <input
            id={id('vat')}
            type="number"
            inputMode="decimal"
            min={0}
            max={99.99}
            step={0.01}
            value={draft.vat_percent}
            onChange={(e) => set({ vat_percent: e.target.value })}
            className={inputClass}
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-5 flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-1.5 min-h-[40px] px-3 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm disabled:opacity-50"
          >
            <FaSave size={12} aria-hidden="true" /> {busy ? 'Saving…' : 'Save plan'}
          </button>
          <p className="text-xs text-slate-500" data-testid="plan-basis-hint">
            {draft.billing_basis === 'flat_monthly'
              ? 'Every truck on the subscription pays the rate every month; only its first month is pro rata from its activation date. Billing ends one calendar month after a written notice.'
              : 'The previous rule: an active truck is one truck for a whole month — truck-days with a valid GPS fix ÷ days in the month.'}{' '}
            Included messages are credited against WhatsApp, then SMS, then email.
          </p>
        </div>
      </form>
      {error && (
        <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
          {notice}
        </p>
      )}
    </section>
  );
}
